import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { useProject } from "../../layout/ProjectLayout";
import { PageHeader, StatusBadge } from "../../components/bits";
import { Switch } from "../../components/ui/switch";
import { Button } from "../../components/ui/button";
import { LibraryBig } from "lucide-react";

const ALL_TOOLS = [
  ["CHARTER", "Project Charter", "PMBOK"], ["RACI", "RACI Matrix", "PMBOK"],
  ["STAKEHOLDER_REGISTER", "Stakeholder Register", "PMBOK"], ["COMMUNICATIONS_PLAN", "Communications Plan", "PMBOK"],
  ["PROJECT_PLAN_MILESTONES", "WBS / Gantt / Milestones", "PMBOK"], ["MEETING_AGENDA_MINUTES", "Meetings", "PMBOK"],
  ["ACTION_DECISION_LOG", "Action & Decision Log", "PMBOK"], ["RAID", "RAID Log", "PMBOK"],
  ["RISK_ISSUE_LOG", "Risk & Issue Log", "PMBOK"], ["DASHBOARD", "Dashboard", "PMBOK"],
  ["STATUS_REPORT", "Status Reports", "PMBOK"], ["RTM", "Requirements (RTM)", "PMBOK"],
  ["REQUIREMENTS_SIGNOFF", "Requirements Sign-off", "PMBOK"], ["DEFECT_LOG", "Defect Log", "PMBOK"],
  ["ACCEPTANCE_CRITERIA", "Acceptance Criteria", "PMBOK"], ["CHANGE_MANAGEMENT", "Change Management", "PMBOK"],
  ["TRAINING_PLAN", "Training Plan", "PMBOK"], ["DATA_MIGRATION_PLAN", "Data Migration Plan", "PMBOK"],
  ["GO_LIVE_CHECKLIST", "Go-Live Checklist", "PMBOK"], ["TRANSITION_PLAN", "Transition Plan", "PMBOK"],
  ["CLOSEOUT_REPORT", "Closeout Report", "PMBOK"],
  ["USER_STORY_BACKLOG", "User Story Backlog", "AGILE"],
  ["FMEA", "FMEA", "SIXSIGMA"],
  ["VALUE_STREAM_MAP", "Value Stream Map", "LEAN"],
  ["A3_PROBLEM_SOLVING", "A3 Problem Solving", "LEAN"],
];

export default function ToolsPage() {
  const { projectId } = useParams();
  const { project, reloadProject } = useProject();
  const [busy, setBusy] = useState(false);

  if (!project) return <div className="text-muted-foreground">Loading…</div>;
  const enabled = new Map((project.enabledTools || []).map((t) => [t.toolCode, t]));

  const toggle = async (code, toolkit) => {
    setBusy(true);
    let tools = [...(project.enabledTools || [])];
    const idx = tools.findIndex((t) => t.toolCode === code);
    if (idx >= 0) tools[idx] = { ...tools[idx], isEnabled: !tools[idx].isEnabled };
    else tools.push({ toolCode: code, toolkit, isEnabled: true, displayOrder: (tools.length + 1) * 10 });
    try {
      await api.put(`/projects/${projectId}/tools`, { enabledTools: tools });
      reloadProject();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const groups = ["PMBOK", "AGILE", "LEAN", "SIXSIGMA"];

  return (
    <div data-testid="tools-page">
      <PageHeader overline="Settings · Cross-toolkit enablement" title="Toolkit Manager"
        actions={
          <Button data-testid="save-as-template-btn" variant="outline" className="rounded-none" disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const { data } = await api.post(`/templates/from-project/${projectId}`, { name: `${project.name} template` });
                toast.success(`Template saved (${data.taskCount} tasks) — see the Template Library`);
              } catch (e) { toast.error(errMsg(e)); }
              setBusy(false);
            }}>
            <LibraryBig className="h-4 w-4 mr-1.5" /> Save as template
          </Button>
        }>
        <p className="text-xs text-muted-foreground mt-1 max-w-lg">Enable individual tools from any methodology toolkit inside this project — e.g. add FMEA (Six Sigma) or User Stories (Agile) to a PMBOK project.</p>
      </PageHeader>
      <div className="grid lg:grid-cols-2 gap-4 max-w-5xl">
        {groups.map((g) => (
          <div key={g} className="border border-border bg-card">
            <div className="px-4 py-2.5 border-b border-border flex items-center gap-2">
              <span className="overline-label">{g}</span>
              {project.primaryMethodology?.toUpperCase() === g && <StatusBadge value="Primary" tone="blue" />}
            </div>
            <div>
              {ALL_TOOLS.filter(([, , tk]) => tk === g).map(([code, label]) => {
                const isOn = enabled.get(code)?.isEnabled;
                return (
                  <div key={code} className="flex items-center justify-between px-4 py-2 border-b border-border last:border-b-0">
                    <span className="text-sm">{label}</span>
                    <Switch data-testid={`tool-switch-${code}`} checked={!!isOn} disabled={busy} onCheckedChange={() => toggle(code, g)} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
